import { BarController, BarElement, CategoryScale, Chart, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js';
import { TYPE_PAIR_FONTS } from '@tallyframe/core';
/**
 * Explicit Chart.js registration.
 *
 * We deliberately do not import `chart.js/auto`: registering only the bar and
 * line pieces keeps a host's bundle honest about what this package can draw,
 * which is exactly the two chart types `ChartWidget.chartType` allows.
 *
 * Everything registered here is a *capability* — a controller, an element, a
 * scale, the legend and tooltip plugins — and registering it is the only way
 * Chart.js lets a chart use it at all. None of it changes how a chart that
 * does not ask for it is drawn, so a host that embeds this package
 * (`examples/embed-standalone`) sees its own charts render exactly as before.
 *
 * Nothing that alters rendering *behaviour* belongs here. This package's two
 * such rules — {@link axisLabelFitPlugin}, and the refusal to rotate an axis
 * label — are design decisions about ReportPad's charts, not improvements to
 * Chart.js, so `buildConfig` (`ChartCanvas.tsx`) attaches both to each chart
 * it builds. A global `Chart.register(axisLabelFitPlugin)` or a write to
 * `Chart.defaults` would reach every chart in the host's page as well.
 */
let registered = false;
export function ensureChartsRegistered() {
    if (registered)
        return;
    Chart.register(BarController, LineController, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Legend, Tooltip);
    registered = true;
}
/** The single character a shortened axis label ends in. One glyph, not three
 *  dots: it costs a third of the width and reads as an elision rather than as
 *  punctuation that might belong to the name. */
export const AXIS_LABEL_ELLIPSIS = '…';
/** `axisLabelFitPlugin`'s id, exported so a test can switch the plugin off for
 *  one chart (`options.plugins['tf-axis-label-fit'] = false`) and compare
 *  against what stock Chart.js would have drawn. */
export const AXIS_LABEL_FIT_PLUGIN_ID = 'tf-axis-label-fit';
/**
 * Sub-pixel slack, in CSS pixels.
 *
 * A scale's gutter is sized by Chart.js as `tick mark + padding*2 + widest
 * label`, and `labelInkBudget` recovers the label's share by subtracting the
 * same two terms back off. That round trip is exact only up to floating-point
 * rounding, and without slack a label that fits its gutter to the last pixel
 * could lose a character to a one-ULP discrepancy. Half a pixel of overshoot
 * is antialiasing; a sliced glyph it is not.
 */
const LABEL_FIT_SLACK = 0.5;
/**
 * Shortens `text` to fit `budget`, ending it in a single {@link
 * AXIS_LABEL_ELLIPSIS}, and returns it untouched when it already fits.
 *
 * `measure` is the caller's text metric — the chart's own canvas context in
 * production, so the budget is spent in the font the label is actually drawn
 * in. It is assumed monotonic in prefix length, which every proportional font
 * is in practice. Cutting happens on code points, so an astral glyph is never
 * split into two broken halves.
 *
 * When not even the ellipsis fits, the bare ellipsis comes back rather than an
 * empty string: a tick with nothing beside it tells the reader less than one
 * that says "there was a name here".
 */
export function fitAxisLabel(text, budget, measure) {
    if (!Number.isFinite(budget))
        return text;
    if (measure(text) <= budget)
        return text;
    const glyphs = Array.from(text);
    let kept = 0;
    let low = 0;
    let high = glyphs.length - 1;
    while (low <= high) {
        const mid = (low + high) >> 1;
        if (measure(glyphs.slice(0, mid).join('') + AXIS_LABEL_ELLIPSIS) <= budget) {
            kept = mid;
            low = mid + 1;
        }
        else {
            high = mid - 1;
        }
    }
    return glyphs.slice(0, kept).join('') + AXIS_LABEL_ELLIPSIS;
}
/** The given scale's options, if it is one of the two cartesian axes — `null`
 *  for anything else. This package only ever builds bar and line charts, so in
 *  practice every scale reaching here is cartesian; the check is what narrows
 *  the base `Scale`'s options to the shape below rather than asserting it, and
 *  it keeps the plugin honest if it is ever attached to a radial axis, which
 *  has no gutter to be clipped by and no row of labels to collide along. */
function cartesianAxis(scale) {
    if (scale.axis !== 'x' && scale.axis !== 'y')
        return null;
    const options = scale.options;
    return options.ticks && options.grid ? options : null;
}
/** How much of a vertical scale's gutter is left for the label's own ink.
 *
 *  Mirrors `Scale.fit`, which spends the gutter on the tick mark, a `padding`
 *  gap on each side of the label, and the label itself. An axis *title* would
 *  take a share too; this package never titles an axis, and were one ever
 *  added the budget would come out generous by the title's height rather than
 *  wrong in the dangerous direction. */
export function labelInkBudget(scale, options) {
    const tickMark = options.grid.drawTicks ? (options.grid.tickLength ?? 0) : 0;
    return scale.width - tickMark - (options.ticks.padding ?? 0) * 2 + LABEL_FIT_SLACK;
}
/** The label a tick will draw, as plain lines of text, or `null` for a tick
 *  Chart.js will not label at all. */
function tickLines(tick) {
    if (typeof tick.label === 'string')
        return [tick.label];
    if (Array.isArray(tick.label))
        return tick.label.map(String);
    return null;
}
/**
 * Shortens the labels of a vertical scale that would otherwise be clipped.
 *
 * Only vertical scales: `Scale._computeLabelArea` clips a left/right axis to
 * its own gutter — which Chart.js caps at half the available width, so a long
 * category name is sliced mid-word with no ellipsis — while a top/bottom axis
 * is only clipped to the canvas, where thinning (below) is the right answer
 * instead.
 */
function shortenClippedLabels(chart, scale, options) {
    if (scale.isHorizontal())
        return;
    const budget = labelInkBudget(scale, options);
    if (!(budget > 0))
        return;
    const internals = scale;
    const { ctx } = chart;
    let shortened = false;
    scale.ticks.forEach((tick, index) => {
        const lines = tickLines(tick);
        if (!lines)
            return;
        ctx.save();
        ctx.font = internals._resolveTickFontOptions(index).string;
        const fitted = lines.map((line) => fitAxisLabel(line, budget, (candidate) => ctx.measureText(candidate).width));
        ctx.restore();
        if (fitted.some((line, i) => line !== lines[i])) {
            tick.label = fitted.length === 1 ? fitted[0] : fitted;
            shortened = true;
        }
    });
    if (shortened)
        internals._labelSizes = null;
}
/**
 * Drops the labels that would be printed on top of their neighbour.
 *
 * Chart.js's own `autoSkip` already thins an axis, but it decides how many
 * ticks fit from `Scale._length` as it stands during `Scale.update` — before
 * `fit` has taken the cross axis's gutter and the layout padding off it. At a
 * narrow width that estimate is generous enough to keep three ticks where two
 * fit, which is how the phone baseline ended up reading
 * "2026-01-022026-01-232026-02-13". By `afterLayout` the axis's real pixel
 * geometry is settled, so the same question can be asked with the right
 * numbers.
 *
 * Only ever *removes* a tick, and only one that genuinely collides: an axis
 * whose labels already clear each other comes back untouched, at every width.
 * The plot area is deliberately left where `fit` put it — this changes which
 * labels are drawn, never where the data is.
 *
 * The test is a plain interval intersection against the last label kept, not
 * "does this one start after that one ended". Tick pixels are monotonic in
 * tick order but not always *ascending*: a linear value axis counts up the
 * screen, so tick 0 sits at the bottom and every later one above it. A
 * one-directional sweep reads that as every label colliding and leaves a value
 * axis with a single number on it.
 */
/**
 * Shortens horizontal category labels to the width of their own slot before
 * {@link dropCollidingLabels} has to drop any. Six clinic names on a narrow
 * chart read better as "Maple…", "Rivers…" than as two survivors out of six.
 * Below a usable slot (three or four glyphs) it leaves the labels whole and
 * lets thinning decide.
 */
const MIN_SLOT_FOR_SHORTENING = 28;
/** Clear space, in px, two neighbouring axis labels keep between them. */
const LABEL_MIN_GAP = 6;
const SLOT_GAP = 6;
function shortenToSlot(chart, scale, options) {
    if (!scale.isHorizontal() || scale.type !== 'category' || scale.ticks.length < 2)
        return;
    // Only when thinning would otherwise have to drop a label: an axis whose
    // labels already clear each other keeps them whole, at every width.
    if (!dropCollidingLabels(chart, scale, options, true, LABEL_MIN_GAP))
        return;
    const slot = scale.width / scale.ticks.length - SLOT_GAP;
    if (slot < MIN_SLOT_FOR_SHORTENING)
        return;
    const internals = scale;
    const { ctx } = chart;
    let shortened = false;
    scale.ticks.forEach((tick, index) => {
        const lines = tickLines(tick);
        if (!lines)
            return;
        ctx.save();
        ctx.font = internals._resolveTickFontOptions(index).string;
        const fitted = lines.map((line) => fitAxisLabel(line, slot, (candidate) => ctx.measureText(candidate).width));
        ctx.restore();
        if (fitted.some((line, i) => line !== lines[i])) {
            tick.label = fitted.length === 1 ? fitted[0] : fitted;
            shortened = true;
        }
    });
    if (shortened)
        internals._labelSizes = null;
}
function dropCollidingLabels(chart, scale, options, dryRun = false, gap = 0) {
    if (scale.ticks.length < 2)
        return false;
    const internals = scale;
    const horizontal = scale.isHorizontal();
    const align = options.ticks.align;
    const { ctx } = chart;
    const kept = [];
    let last;
    scale.ticks.forEach((tick, index) => {
        const centre = scale.getPixelForTick(index);
        const lines = tickLines(tick);
        if (lines === null || !Number.isFinite(centre)) {
            kept.push(tick);
            return;
        }
        const font = internals._resolveTickFontOptions(index);
        ctx.save();
        ctx.font = font.string;
        const extent = horizontal
            ? Math.max(...lines.map((line) => ctx.measureText(line).width))
            : lines.length * font.lineHeight;
        ctx.restore();
        // Along a horizontal axis the label sits where `ticks.align` puts it;
        // down a vertical one it is always centred on its tick (baseline
        // 'middle'), which is the same arithmetic as align 'center'.
        const anchor = horizontal ? align : 'center';
        const start = anchor === 'start' ? centre : anchor === 'end' ? centre - extent : centre - extent / 2;
        const end = start + extent;
        // `gap` asks for air between labels, not just no overlap: on a category
        // axis that opted out of autoSkip, two names that merely touch
        // ("Maple StEastgate") would otherwise read as one.
        if (last && start < last.end + gap && last.start < end + gap)
            return;
        last = { start, end };
        kept.push(tick);
    });
    const collided = kept.length !== scale.ticks.length;
    if (collided && !dryRun) {
        scale.ticks = kept;
        internals._labelSizes = null;
    }
    return collided;
}
/**
 * Keeps axis labels readable at narrow widths without rotating them.
 *
 * Runs in `afterLayout`, the first hook at which every scale's final pixel
 * geometry exists (`Chart._updateLayout` calls `box.configure()` on each of
 * them immediately before firing it), and the last one before the tick labels
 * and grid lines are computed for drawing — both caches were cleared at the
 * top of `Scale.update` and are filled lazily on the way to the canvas, so a
 * label rewritten or a tick removed here is what actually gets painted.
 *
 * `buildConfig` (`ChartCanvas.tsx`) puts this in each chart's own `plugins`
 * array rather than `ensureChartsRegistered()` handing it to `Chart.register`:
 * a host embedding this package draws Chart.js charts of its own, and however
 * defensible this pass is, running it on someone else's chart uninvited is not
 * ours to decide.
 */
export const axisLabelFitPlugin = {
    id: AXIS_LABEL_FIT_PLUGIN_ID,
    afterLayout(chart, _args, pluginOptions) {
        for (const scale of Object.values(chart.scales)) {
            const options = cartesianAxis(scale);
            if (!options)
                continue;
            shortenClippedLabels(chart, scale, options);
            // Off for date dimensions (a shortened date is a wrong date) and for
            // any chart that did not ask: `buildConfig` turns it on per chart.
            const categories = pluginOptions?.shortenCategories === true && scale.isHorizontal() && scale.type === 'category';
            if (categories)
                shortenToSlot(chart, scale, options);
            dropCollidingLabels(chart, scale, options, false, categories ? LABEL_MIN_GAP : 0);
        }
    }
};
/**
 * Chart drawing constants (spec §7.1–7.4).
 *
 * Chart.js draws on a `<canvas>`, which cannot read CSS custom properties, so
 * these mirror the report tokens in `tokens.css` as literal hex values rather
 * than `var(--rp-*)` references. Series colors themselves are not among
 * them — `buildConfig` (`ChartCanvas.tsx`) takes those from the resolved
 * brand's palette instead, which is what replaced the old flat
 * `SERIES_COLORS` array this module used to export.
 */
export const CHART_INK = '#16181B'; // mirrors --rp-ink
export const CHART_MUTED = '#5F6670'; // mirrors --rp-muted
export const CHART_GRID = '#ECEEF1'; // mirrors --rp-grid
export const CHART_NEUTRAL = '#8A919A'; // mirrors --rp-axis / --rp-series-2
/** The report's paper color — Chart.js has no fixed-pixel gap between grouped
 *  bars, so `buildConfig` insets each one with a paper-colored border on the
 *  side facing its neighbor instead (spec §7.2). */
export const CHART_PAPER = '#FFFFFF'; // mirrors --rp-paper
export const DEFAULT_CHART_COLORS = {
    ink: CHART_INK,
    muted: CHART_MUTED,
    grid: CHART_GRID,
    paper: CHART_PAPER,
    neutral: CHART_NEUTRAL
};
/** Reads the report tokens from `el`'s computed style, falling back to the
 *  literal mirrors one token at a time. */
export function resolveChartColors(el) {
    if (typeof getComputedStyle !== 'function')
        return DEFAULT_CHART_COLORS;
    const style = getComputedStyle(el);
    const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback;
    return {
        ink: read('--rp-ink', CHART_INK),
        muted: read('--rp-muted', CHART_MUTED),
        grid: read('--rp-grid', CHART_GRID),
        paper: read('--rp-paper', CHART_PAPER),
        neutral: read('--rp-axis', CHART_NEUTRAL)
    };
}
/**
 * Formats a value-axis tick in the widget's own number format, so a percent
 * chart's axis reads "4%" rather than "0.04" and a currency axis carries its
 * symbol. Decimals follow the tick step, not the widget's display decimals:
 * an axis stepping by 10,000 needs none, one stepping by 0.5% needs one.
 */
export function axisTickDecimals(step, style) {
    const scaled = style === 'percent' ? step * 100 : step;
    if (!(scaled > 0) || !Number.isFinite(scaled))
        return 0;
    return Math.min(2, Math.max(0, -Math.floor(Math.log10(scaled) + 1e-9)));
}
/** The report font, as a literal fallback for when `--rp-font` can't be read
 *  from the canvas's computed style (no stylesheet loaded, as in tests). */
export const CHART_FONT_FAMILY = TYPE_PAIR_FONTS.plex.body;
//# sourceMappingURL=chart-setup.js.map